import React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Cog, Loader2 } from "lucide-react";

import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { workOrdersService } from "@/services/work_orders.service";
import { workshopService } from "@/services/workshop.service";
import { getErrorMessage } from "@/services/api";
import api from "@/services/api";

/**
 * Modal shown when the user clicks "Finalizar OS" on the WO details page.
 * Two paths (per user decision 3b):
 *  - Finalizei em campo   → POST /work-orders/{id}/finish      (status → finalizada)
 *  - Enviar para oficina  → POST /work-orders/{id}/send-to-workshop
 *                           (WO stays em_atendimento, workshop entry created)
 */
export function FinishOrSendToWorkshopDialog({
    open, onOpenChange, workOrderId, workOrderNumber,
}) {
    const qc = useQueryClient();
    const navigate = useNavigate();

    const finish = useMutation({
        mutationFn: () => workOrdersService.finish(workOrderId),
        onSuccess: () => {
            toast.success("OS finalizada em campo.");
            qc.invalidateQueries({ queryKey: ["work_order", workOrderId] });
            qc.invalidateQueries({ queryKey: ["work_orders"] });
            qc.invalidateQueries({ queryKey: ["dashboard"] });
            onOpenChange(false);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao finalizar OS.")),
    });

    const sendToWorkshop = useMutation({
        mutationFn: async () => {
            const { data } = await api.post(`/work-orders/${workOrderId}/send-to-workshop`);
            return data;
        },
        onSuccess: (res) => {
            toast.success(`Equipamento enviado para a oficina (${workOrderNumber}).`);
            qc.invalidateQueries({ queryKey: ["work_order", workOrderId] });
            qc.invalidateQueries({ queryKey: ["workshops"] });
            qc.invalidateQueries({ queryKey: ["workshop-dashboard"] });
            onOpenChange(false);
            const wsId = res?.workshop?.id;
            if (wsId) {
                setTimeout(() => navigate(`/workshop/${wsId}`), 200);
            }
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao enviar para oficina.")),
    });

    const busy = finish.isPending || sendToWorkshop.isPending;

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="sm:max-w-lg" data-testid="finish-or-send-dialog">
                <DialogHeader>
                    <DialogTitle>Como você deseja finalizar a OS?</DialogTitle>
                    <DialogDescription className="text-xs">
                        Escolha se o atendimento foi concluído em campo ou se o equipamento precisa ir até a oficina para diagnóstico técnico.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 py-2">
                    <button
                        type="button"
                        onClick={() => finish.mutate()}
                        disabled={busy}
                        className="group flex items-start gap-3 rounded-lg border p-4 text-left transition hover:border-emerald-500/60 disabled:opacity-60"
                        data-testid="finish-in-field-option"
                    >
                        <div className="grid h-10 w-10 place-items-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                            {finish.isPending
                                ? <Loader2 className="h-5 w-5 animate-spin" />
                                : <CheckCircle2 className="h-5 w-5" />}
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-semibold">Finalizei em campo</div>
                            <div className="text-xs text-muted-foreground">
                                A OS será marcada como <span className="font-medium">Finalizada</span>.
                            </div>
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => sendToWorkshop.mutate()}
                        disabled={busy}
                        className="group flex items-start gap-3 rounded-lg border p-4 text-left transition hover:border-secondary/60 disabled:opacity-60"
                        data-testid="send-to-workshop-option"
                    >
                        <div className="grid h-10 w-10 place-items-center rounded-md bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">
                            {sendToWorkshop.isPending
                                ? <Loader2 className="h-5 w-5 animate-spin" />
                                : <Cog className="h-5 w-5" />}
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-semibold">Enviar para oficina</div>
                            <div className="text-xs text-muted-foreground">
                                Será criado um registro na Oficina para recepção e diagnóstico técnico. A OS continua em <span className="font-medium">Em Atendimento</span>.
                            </div>
                        </div>
                    </button>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default FinishOrSendToWorkshopDialog;
